import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  getReminders, removeReminder, fireNotification, msUntilFire,
} from "../lib/reminders";
import { logger } from "../lib/logger";

const ReminderContext = createContext({ reminders: [], bump: () => {} });

export const useReminders = () => useContext(ReminderContext);

/**
 * Wraps the app; schedules native Web Notifications for every pending reminder.
 * Rescans the storage on changes and on tab focus to catch reminders set in other tabs.
 */
export const ReminderProvider = ({ children }) => {
  const [reminders, setReminders] = useState(() => getReminders());
  const timers = useRef(new Map());

  const refresh = useCallback(() => {
    setReminders(getReminders());
  }, []);

  const schedule = useCallback((rems) => {
    // Clear previous timers
    timers.current.forEach(t => clearTimeout(t));
    timers.current = new Map();
    const now = Date.now();
    rems.forEach(r => {
      const delay = msUntilFire(r.fireAt);
      if (delay < -60_000) {
        // Older than 1 min — drop silently
        removeReminder(r.videoId, r.fireAt);
        return;
      }
      const effective = Math.max(0, delay);
      const t = setTimeout(() => {
        fireNotification({
          title: `${r.channelName} · maintenant`,
          body: r.title,
          url: `/watch/${r.videoId}`,
        });
        toast.success(`📺 ${r.title}`, {
          description: `${r.channelName} · ça commence maintenant`,
          duration: 8000,
          action: { label: "Regarder", onClick: () => { window.location.href = `/watch/${r.videoId}`; } },
        });
        removeReminder(r.videoId, r.fireAt);
        refresh();
      }, effective);
      timers.current.set(r.id, t);
    });
  }, [refresh]);

  // Schedule whenever the list changes
  useEffect(() => {
    schedule(reminders);
    return () => timers.current.forEach(t => clearTimeout(t));
  }, [reminders, schedule]);

  // Listen for storage updates from our own helpers + other tabs
  useEffect(() => {
    const onUpdate = () => refresh();
    const onStorage = (e) => { if (e.key === "fedevo:reminders") refresh(); };
    const onVisibility = () => { if (document.visibilityState === "visible") refresh(); };
    window.addEventListener("fedevo:reminders-updated", onUpdate);
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("fedevo:reminders-updated", onUpdate);
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refresh]);

  return (
    <ReminderContext.Provider value={{ reminders, bump: refresh }}>
      {children}
    </ReminderContext.Provider>
  );
};

export default ReminderProvider;
